import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import ProductShowcase from '@/components/base/ProductShowcase';
import { useSchemaLD } from '@/hooks/useSEO';
import { waLink, SITE_URL } from '@/config/site';
import { pracaAlimentacao, avisoImagens, avisoProjetos } from '@/mocks/curadoria';

const operacoes = [
  {
    icon: 'ri-store-3-line',
    titulo: 'Shopping centers',
    descricao: 'Áreas de alimentação com circulação intensa e necessidade de padronização.',
  },
  {
    icon: 'ri-restaurant-2-line',
    titulo: 'Restaurantes e redes',
    descricao: 'Salões, varandas e áreas externas com repetição de conjunto.',
  },
  {
    icon: 'ri-plane-line',
    titulo: 'Aeroportos e rodoviárias',
    descricao: 'Grandes fluxos, permanência curta e manutenção constante.',
  },
  {
    icon: 'ri-building-2-line',
    titulo: 'Centros comerciais e corporativos',
    descricao: 'Praças internas e refeitórios de prédios e indústrias.',
  },
];

const criterios = [
  'Classificação de uso intensivo mantida conforme descrita no catálogo do fabricante.',
  'Nenhum índice de resistência é divulgado sem ficha técnica.',
  'Cadeiras, mesas e bancos avaliados por conjunto: 1 imagem ambientada + 1 recorte isolado.',
  'Fotografias de ambientes de terceiros são referência de aplicação, não projeto executado.',
];

export default function PracasAlimentacaoPage() {
  useSchemaLD({
    id: 'schema-pracas-alimentacao',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Mobiliário para praças de alimentação',
      description:
        'Cadeiras, banquetas, mesas, booths e complementos para praças de alimentação, refeitórios e operações coletivas. Linha Artesian com modelos identificados.',
      url: `${SITE_URL}/mobiliario-pracas-alimentacao`,
      isPartOf: { '@type': 'WebSite', name: 'Home Office Premium', url: SITE_URL },
      about: 'Mobiliário para praças de alimentação e food service',
    },
  });

  useSchemaLD({
    id: 'schema-pracas-alimentacao-breadcrumb',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Soluções', item: `${SITE_URL}/produtos` },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Praças de alimentação',
          item: `${SITE_URL}/mobiliario-pracas-alimentacao`,
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-16">
        {/* HERO */}
        <section className="bg-foreground-950 text-background-50">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 pt-10 md:pt-16 pb-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-end">
              <div className="lg:col-span-6 pb-10 md:pb-16">
                <nav
                  aria-label="Você está aqui"
                  className="flex flex-wrap items-center gap-2 text-[11.5px] text-background-50/45"
                >
                  <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
                    Home
                  </Link>
                  <i className="ri-arrow-right-s-line text-[12px]"></i>
                  <Link
                    to="/produtos"
                    className="hover:text-background-50 transition-colors cursor-pointer"
                  >
                    Soluções
                  </Link>
                  <i className="ri-arrow-right-s-line text-[12px]"></i>
                  <span className="text-background-50/80">Praças de alimentação</span>
                </nav>

                <h1 className="mt-6 text-[32px] sm:text-[42px] md:text-[52px] leading-[1.04] tracking-[-0.038em] font-semibold text-background-50">
                  Ambientes preparados para receber mais.
                </h1>

                <p className="mt-5 md:mt-6 text-[15px] md:text-[17px] leading-[1.6] text-background-50/60 max-w-[500px]">
                  Cadeiras, mesas e soluções de mobiliário para praças de alimentação,
                  restaurantes, shopping centers e ambientes de grande circulação.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                  <a
                    href={waLink('pracas')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-background-50 text-foreground-950 text-[14px] md:text-[15px] font-medium px-6 py-3.5 rounded-full hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-[15px]"></i>
                    Consultar cadeiras e mesas
                  </a>
                  <Link
                    to="/mobiliario-urbano"
                    className="inline-flex items-center justify-center gap-1.5 border border-white/20 text-background-50 text-[14px] md:text-[15px] font-medium px-6 py-3.5 rounded-full hover:border-white/50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Mobiliário urbano
                    <i className="ri-arrow-right-line text-[12px]"></i>
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6">
                <figure className="m-0">
                  <div className="w-full aspect-[16/10] lg:aspect-[5/4] overflow-hidden rounded-t-[18px] md:rounded-t-[24px] bg-foreground-900">
                    <img
                      src="/media/projetos/edd9cec9-img_4245.webp"
                      alt="Praça de alimentação do Caruaru Shopping mobiliada pela Home Office Premium, com mesas e cadeiras"
                      title="Praça de alimentação entregue pela HomeOffice Premium — Caruaru Shopping"
                      className="w-full h-full object-cover object-center"
                      fetchPriority="high"
                    />
                  </div>
                  <figcaption className="mt-3 text-[11px] leading-relaxed text-background-50/40">
                    Projeto entregue pela Home Office Premium — praça de alimentação, Caruaru (PE).
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>

        {/* OPERAÇÕES */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-12 md:py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8">
              {operacoes.map((item) => (
                <div key={item.titulo} className="border-t border-background-300 pt-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-background-200 text-secondary-600">
                    <i className={`${item.icon} text-[15px]`}></i>
                  </span>
                  <h2 className="mt-3 text-[14.5px] font-medium tracking-[-0.02em] text-foreground-950">
                    {item.titulo}
                  </h2>
                  <p className="mt-1.5 text-[12.5px] leading-[1.6] text-foreground-500">
                    {item.descricao}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GRID */}
        <section className="bg-white">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-14 items-end mb-10 md:mb-14">
              <div className="lg:col-span-7">
                <p className="hop-eyebrow text-foreground-400">Linha Artesian · Praças e coletivos</p>
                <h2 className="mt-4 text-[26px] sm:text-[32px] md:text-[40px] leading-[1.08] tracking-[-0.036em] font-semibold text-foreground-950">
                  O conjunto completo da operação.
                </h2>
              </div>
              <div className="lg:col-span-5">
                <p className="text-[14px] md:text-[15px] leading-[1.65] text-foreground-500 max-w-[420px]">
                  Assentos, mesas, booths e complementos retirados das páginas 3 a 28 do catálogo
                  Artesian 2026. Nenhum preço é gerado automaticamente.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 md:gap-x-8 md:gap-y-16">
              {pracaAlimentacao.map((produto) => (
                <ProductShowcase
                  key={produto.nome}
                  produto={produto}
                  contexto="pracas-de-alimentacao"
                  mostrarValidacao
                />
              ))}
            </div>
          </div>
        </section>

        {/* CRITÉRIOS */}
        <section className="bg-background-50 border-t border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
              <div className="lg:col-span-5">
                <p className="hop-eyebrow text-foreground-400">Critério de seleção</p>
                <h2 className="mt-4 text-[26px] sm:text-[32px] md:text-[38px] leading-[1.08] tracking-[-0.036em] font-semibold text-foreground-950">
                  Uso intensivo não é adjetivo de vitrine.
                </h2>
                <p className="mt-4 text-[14px] md:text-[15px] leading-[1.65] text-foreground-500 max-w-[420px]">
                  A indicação de uso vem do catálogo do fabricante. Quando não há confirmação,
                  tratamos como pendência de validação técnica — não como argumento de venda.
                </p>
                <a
                  href={waLink('pracas')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center justify-center gap-2 bg-foreground-950 text-white text-[14px] font-medium px-6 py-3 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-[15px]"></i>
                  Falar com um consultor
                </a>
              </div>

              <div className="lg:col-span-7">
                <ul className="border-t border-background-200">
                  {criterios.map((criterio, index) => (
                    <li
                      key={criterio}
                      className="flex items-start gap-4 py-4 border-b border-background-200"
                    >
                      <span className="mt-[3px] text-[11px] font-medium tracking-[0.14em] text-foreground-300">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[13.5px] leading-[1.6] text-foreground-700">
                        {criterio}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* AVISOS */}
        <section className="bg-white border-t border-background-200">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10 py-10 md:py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              <p className="flex items-start gap-2.5 text-[11.5px] leading-[1.65] text-foreground-400">
                <i className="ri-image-line mt-[1px] text-[14px] text-foreground-300"></i>
                <span>{avisoImagens}</span>
              </p>
              <p className="flex items-start gap-2.5 text-[11.5px] leading-[1.65] text-foreground-400">
                <i className="ri-shield-check-line mt-[1px] text-[14px] text-foreground-300"></i>
                <span>{avisoProjetos}</span>
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-16 md:pb-24">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8 xl:px-10">
            <div className="rounded-[20px] md:rounded-[26px] bg-accent-500 px-6 sm:px-10 md:px-14 py-12 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <h2 className="text-[24px] sm:text-[28px] md:text-[34px] leading-[1.1] tracking-[-0.035em] font-semibold text-background-50 max-w-[540px]">
                  Padronize a praça inteira em um único pedido.
                </h2>
                <p className="mt-3 text-[14px] leading-[1.6] text-background-50/80 max-w-[460px]">
                  Cadeiras, mesas, banquetas e complementos na mesma linguagem visual, com
                  quantitativos fechados por ambiente.
                </p>
              </div>
              <Link
                to="/orcamento"
                className="inline-flex items-center justify-center bg-background-50 text-foreground-950 text-[14px] font-medium px-7 py-3.5 rounded-full hover:bg-white transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                Solicitar orçamento por ambiente
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}